import { useQuery } from '@tanstack/react-query';
import z from 'zod';

import { authorizedAxiosInstance } from '@/clients';

const searchCountMetadataSchema = z
    .object({
        asOf: z.string().datetime(),
        count: z.number().int().nonnegative(),
        source: z.string().trim().min(1),
    })
    .strict();
const searchMetadataSchema = z
    .object({
        counts: z.record(z.string(), searchCountMetadataSchema),
    })
    .strict();

export type SearchMetadata = z.infer<typeof searchMetadataSchema>;

const relationsSearchMetadataSchema = z
    .object({
        allDataSources: z.number().int().nonnegative(),
        asOf: z.string().datetime(),
        companyRecords: z.number().int().nonnegative(),
        emailAndCalendarDataSources: z.number().int().nonnegative(),
        peopleRecords: z.number().int().nonnegative(),
        peopleCount: z
            .object({
                complete: z.boolean(),
                lastStartedAt: z.string().datetime().nullable(),
                lastSyncedAt: z.string().datetime().nullable(),
                refreshing: z.boolean(),
                status: z.enum(['pending', 'syncing', 'ready', 'error']),
            })
            .strict(),
    })
    .strict();
const connectedDocumentCountSchema = z
    .object({
        asOf: z.string().datetime(),
        complete: z.boolean(),
        count: z.number().int().nonnegative(),
        countedSources: z.number().int().nonnegative(),
        source: z.literal('connected-accounts'),
        totalSources: z.number().int().nonnegative(),
        uncountedSourceTypes: z.array(z.string()),
    })
    .strict();
const workspaceSearchMetadataSchema = z
    .object({
        counts: z
            .object({
                documents: connectedDocumentCountSchema,
            })
            .strict(),
    })
    .strict();

const fetchSearchMetadata = async (signal: AbortSignal): Promise<SearchMetadata> => {
    const response = await authorizedAxiosInstance.get('/api/search/vendors/fmp/metadata', { signal });

    return searchMetadataSchema.parse(response.data);
};

export const useSearchMetadataQuery = () =>
    useQuery({
        gcTime: 24 * 60 * 60 * 1_000,
        queryFn: ({ signal }) => fetchSearchMetadata(signal),
        queryKey: ['search-metadata'],
        retry: 1,
        staleTime: 6 * 60 * 60 * 1_000,
    });

const fetchRelationsSearchMetadata = async (
    signal: AbortSignal,
): Promise<z.infer<typeof relationsSearchMetadataSchema>> => {
    const response = await authorizedAxiosInstance.get('/api/relations/metadata', {
        signal,
    });

    return relationsSearchMetadataSchema.parse(response.data);
};

export const useRelationsSearchMetadataQuery = (workspaceId?: string) =>
    useQuery({
        enabled: Boolean(workspaceId),
        gcTime: 60 * 60 * 1_000,
        queryFn: ({ signal }) => fetchRelationsSearchMetadata(signal),
        queryKey: ['search-metadata', 'relations', workspaceId],
        refetchInterval: (query) => {
            const peopleCount = query.state.data?.peopleCount;

            if (peopleCount?.refreshing || peopleCount?.status === 'syncing') {
                return 3_000;
            }

            if (peopleCount?.status === 'error' && !peopleCount.complete) {
                return 60_000;
            }

            return false;
        },
        refetchOnMount: 'always',
        retry: 1,
        staleTime: 0,
    });

const fetchWorkspaceSearchMetadata = async (
    signal: AbortSignal,
): Promise<z.infer<typeof workspaceSearchMetadataSchema>> => {
    const response = await authorizedAxiosInstance.get('/api/search/database/workspaces/metadata', { signal });

    return workspaceSearchMetadataSchema.parse(response.data);
};

export const useWorkspaceSearchMetadataQuery = (workspaceId?: string) =>
    useQuery({
        enabled: Boolean(workspaceId),
        gcTime: 60 * 60 * 1_000,
        queryFn: ({ signal }) => fetchWorkspaceSearchMetadata(signal),
        queryKey: ['search-metadata', 'workspace', workspaceId],
        retry: 1,
        staleTime: 15 * 60 * 1_000,
    });
